import { useEffect, useState } from 'react'
import { Button, Drawer, EmptyState, SearchInput, toast } from '@/components/ui'
import { useLibrary, useUpdateItem } from '../api/queries'
import type { QuestionnaireItem } from '../api/types'
import { CHOICE_LABEL } from '../utils/meta'

export interface LibraryPickerDrawerProps {
  questionnaireId: string
  item: QuestionnaireItem | null
  onClose: () => void
}

/** Search the library and answer the item with an entry's answer. */
export function LibraryPickerDrawer({ questionnaireId, item, onClose }: LibraryPickerDrawerProps) {
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  useEffect(() => {
    const id = setTimeout(() => setDebounced(search), 200)
    return () => clearTimeout(id)
  }, [search])

  const { data, isLoading } = useLibrary({ q: debounced || undefined })
  const update = useUpdateItem()
  const entries = data?.items ?? []

  function use(entryId: string) {
    if (!item) return
    update.mutate(
      { questionnaireId, itemId: item.id, use_entry_id: entryId },
      {
        onSuccess: onClose,
        onError: (err) =>
          toast.danger(`Couldn't use that answer${err instanceof Error ? `: ${err.message}` : ''}`),
      },
    )
  }

  return (
    <Drawer
      open={item !== null}
      onOpenChange={(o) => !o && onClose()}
      size="wide"
      title="Use a library answer"
      meta={item ? `For: ${item.question_text}` : undefined}
    >
      <div className="flex flex-col gap-3">
        <SearchInput
          placeholder="Search questions and answers…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search the library"
          autoFocus
        />
        {entries.length === 0 && !isLoading ? (
          <EmptyState variant="no-match" title="Nothing in the library matches" />
        ) : (
          entries.map((e) => (
            <div key={e.id} className="rounded-md border border-subtle p-3">
              <p className="font-medium">{e.canonical_question}</p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-secondary">
                {e.answer_choice ? <strong>{CHOICE_LABEL[e.answer_choice]} — </strong> : null}
                {e.answer_text || '—'}
              </p>
              <div className="mt-2 flex items-center gap-3">
                <Button
                  size="sm"
                  variant="secondary"
                  loading={update.isPending}
                  onClick={() => use(e.id)}
                >
                  Use this answer
                </Button>
                <span className="text-xs text-tertiary">
                  Used {e.use_count}× · {e.variant_count ?? 1} wordings
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </Drawer>
  )
}
